import { type SubscriptionTier } from '@heidi/schema/ml-scribe';

import { useQueryUserBillingInfo } from '../../data/query/useQueryUserBillingInfo';
import { isClinicianMaxBillingSeat } from './is-clinician-max-billing-seat';
import {
  isCreditUsageSurfaceEligible,
  isCreditUsageSurfaceEnabled,
  isHeidi2OptInCreditUsageEnabled,
} from './is-credit-usage-surface-enabled';

export type CreditUsageSurfaceFlags = {
  /** `release-heidi-2-opt-in` (app-resolved) — rollout switch for the Heidi 2 arm. */
  isHeidi2OptInEnabled: boolean;
  /** `release-heidi-2-clinician-max-beta` (app-resolved) — beta cohort. */
  isHeidi2ClinicianMaxBetaEnabled: boolean;
};

/** Beta flag OR (opt-in flag AND cached billing `heidi_version === 2`). */
export function useIsCreditUsageSurfaceEnabled({
  isHeidi2OptInEnabled,
  isHeidi2ClinicianMaxBetaEnabled,
}: CreditUsageSurfaceFlags): boolean {
  const { data: billingInfo } = useQueryUserBillingInfo();
  return isCreditUsageSurfaceEnabled({
    isHeidi2OptInEnabled,
    isHeidi2ClinicianMaxBetaEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
}

/** The surface flags AND the tier gate, so a caller needs no billing read of its own. */
export function useIsCreditUsageSurfaceEligible({
  isHeidi2OptInEnabled,
  isHeidi2ClinicianMaxBetaEnabled,
  newUserTier,
}: CreditUsageSurfaceFlags & {
  newUserTier: SubscriptionTier | null | undefined;
}): boolean {
  const { data: billingInfo } = useQueryUserBillingInfo();
  return isCreditUsageSurfaceEligible({
    isHeidi2OptInEnabled,
    isHeidi2ClinicianMaxBetaEnabled,
    heidiVersion: billingInfo?.heidi_version,
    newUserTier,
  });
}

/**
 * The sidebar-footer verdict: the Heidi 2 opt-in arm always renders on any
 * tier, everyone else falls back to the tier gate. Owns the billing read so
 * the sidebar slot needs no billing hook of its own.
 *
 * A Clinician Max seat has nothing to upgrade to until Practice Max checkout
 * is live, so the tile stays hidden for that seat while
 * `release-heidi-2-practice-max-upgrade` is off.
 */
export function useShouldRenderSidebarCreditUsagePopover({
  isHeidi2OptInEnabled,
  isHeidi2ClinicianMaxBetaEnabled,
  isPracticeMaxUpgradeEnabled,
  newUserTier,
}: CreditUsageSurfaceFlags & {
  /** `release-heidi-2-practice-max-upgrade` (app-resolved, PostHog). */
  isPracticeMaxUpgradeEnabled: boolean;
  newUserTier: SubscriptionTier | null | undefined;
}): boolean {
  const { data: billingInfo } = useQueryUserBillingInfo();
  const heidiVersion = billingInfo?.heidi_version;

  if (!isPracticeMaxUpgradeEnabled && isClinicianMaxBillingSeat(billingInfo)) {
    return false;
  }

  if (isHeidi2OptInCreditUsageEnabled({ isHeidi2OptInEnabled, heidiVersion })) {
    return true;
  }

  return isCreditUsageSurfaceEligible({
    isHeidi2OptInEnabled,
    isHeidi2ClinicianMaxBetaEnabled,
    heidiVersion,
    newUserTier,
  });
}
